import {
  CloudDownloadOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { Button, Space, Tag, Tooltip, message } from 'antd';
import download from 'downloadjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';
import CustomCopyable from '../../components/CustomCopyable';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import { CONFIG } from '../../constants';
import UserContext from '../../context/user-context';
import { b64toBlob } from '../../functions/images';
import { ESTADOS_INSPECCION, errorHandler, toUpperCase } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';

const FileSaver = require('file-saver');

function InspeccionList() {
  const { user } = useContext(UserContext);
  const [columns, setColumns] = useState([]);
  const [loadingId, setLoadingId] = useState(false);
  const [loadingPDFId, setLoadingPDFId] = useState(false);
  const tableRef = useRef();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const history = useHistory();

  const handleGetNewToken = async (tokenOriginal) => {
    try {
      const response = await instanceRef.instance.post(
        `/tokenExpirables/regenerar-from-token-original?flujo=inspeccion&tokenOriginal=${tokenOriginal}`
      );
      if (response.data) {
        navigator.clipboard.writeText(
          `${window.location.origin}/inspecciones-publico/form/${response.data.message}`
        );
        message.success('Se copió el enlace al portapapeles');
      }
    } catch (err) {
      errorHandler('Error al cargar la solicitud', err);
    }
  };
  function getCopyButtonComponent(record) {
    if (record.ampliacion) {
      return (
        <Button
          type="text"
          icon={
            <CustomCopyable
              copyable={{
                text: `${window.location.origin}/inspecciones/ampliacion/cliente/${record.ampliacion.token}`,
                tooltips: [
                  'Copiar enlace para subir fotos de ampliación',
                  'Copiado'
                ]
              }}
            />
          }
          size="middle"
        />
      );
    }
    return (
      <Tooltip title="Copiar enlace para subir fotos">
        <Button
          type="text"
          icon={<CopyOutlined style={{ color: CONFIG.COLOR_PRIMARY }} />}
          size="middle"
          onClick={() => handleGetNewToken(record.token)}
        />
      </Tooltip>
    );
  }

  useEffect(() => {
    let columnas;
    if (user && user['cognito:groups'][0] === 'ADMIN_ASEGURADORA') {
      columnas = [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: '10px',
          filterType: fieldTypes.NUMERIC,
          sortable: true
        },
        {
          title: 'Corredora',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'empresa',
            'ec',
            'nombre'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Empresa',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'empresa',
            'nombre_corto'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Cliente',
          dataIndex: ['solicitud', 'cotizacion', 'persona', 'nombres'],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Marca',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'marca',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Modelo',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'modelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'SubModelo',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'submodelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Año',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'aniosubmodelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Chapa',
          dataIndex: ['solicitud', 'cotizacion', 'vehiculo', 'chapa'],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Fecha Solicitud',
          dataIndex: 'fecha_solicitud_inspeccion',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Fecha Fotografía',
          dataIndex: 'fecha_subida_fotos',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Fecha Informe',
          dataIndex: 'fecha_informe_inspeccion',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Estado',
          dataIndex: 'estado',
          render: (estado) => {
            const estadoUpperCase = toUpperCase(estado);
            let color = 'white';
            if (estadoUpperCase === 'PENDIENTE') {
              color = 'lightgray';
            } else if (estadoUpperCase === 'INICIADA') {
              color = 'yellow';
            } else if (estadoUpperCase === 'FOTOS_SUBIDAS') {
              color = 'blue';
            } else if (estadoUpperCase === 'AMPLIACION_PENDIENTE') {
              color = 'orange';
            } else if (estadoUpperCase === 'FINALIZADA') {
              color = 'green';
            }
            return (
              <Tag color={color} key={estadoUpperCase}>
                {estadoUpperCase}
              </Tag>
            );
          },
          filterType: fieldTypes.ENUM,
          filters: Object.keys(ESTADOS_INSPECCION).map((key) => ({
            text: ESTADOS_INSPECCION[key],
            value: key
          })),
          sortable: true
        },
        {
          title: 'Resultado',
          dataIndex: 'resultado',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => {
            const viaWhatsapp =
              record.solicitud?.cotizacion?.aseguradoraEmpresa?.empresa
                ?.tiene_whatsapp &&
              record.solicitud.cotizacion.persona.via_contacto === 'whatsapp';

            return (
              <Space size="small">
                <Tooltip title="Ver Informe">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    disabled={record.estado === 'PENDIENTE'}
                    onClick={() =>
                      handleOpenUrl(`inspecciones/edit/${record.token}`)
                    }
                  />
                </Tooltip>
                <Tooltip title="Descargar pdf">
                  <Button
                    type="text"
                    icon={<FilePdfOutlined />}
                    size="middle"
                    loading={loadingPDFId === record.id}
                    disabled={!(record.estado === 'FINALIZADA')}
                    onClick={() => downloadPDF(record)}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    record.estado === 'PENDIENTE'
                      ? 'Sin Fotografías'
                      : 'Descargar Fotografías'
                  }
                >
                  <Button
                    type="text"
                    icon={<CloudDownloadOutlined />}
                    size="middle"
                    loading={loadingId === record.id}
                    disabled={record.estado === 'PENDIENTE'}
                    onClick={() => downloadFiles(record, false, false)}
                  />
                </Tooltip>
                <>
                  <Tooltip
                    title={
                      record.estado === 'FOTOS_SUBIDAS' ||
                      record.solicitud.cotizacion.tipo_venta !== 'presencial'
                        ? `No es posible enviar ${
                            viaWhatsapp ? 'Whatsapp' : 'SMS'
                          }`
                        : `Enviar por ${
                            viaWhatsapp ? 'Whatsapp' : 'SMS'
                          } el enlace para subir fotos`
                    }
                  >
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => {
                        sendSms(record);
                      }}
                      icon={<MessageOutlined />}
                      size="middle"
                      disabled={
                        record.estado === 'FOTOS_SUBIDAS' ||
                        record.estado === 'FINALIZADA' ||
                        record.solicitud.cotizacion.tipo_venta !== 'presencial'
                      }
                    />
                  </Tooltip>
                  {record.estado === 'FOTOS_SUBIDAS' ||
                  record.estado === 'FINALIZADA' ? (
                    <Tooltip title="No es posible copiar enlace">
                      <Button
                        disabled
                        icon={<CopyOutlined />}
                        type="text"
                        size="middle"
                      />
                    </Tooltip>
                  ) : (
                    getCopyButtonComponent(record)
                  )}
                </>
              </Space>
            );
          }
        }
      ];
    } else {
      columnas = [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: '10px',
          filterType: fieldTypes.NUMERIC,
          sortable: true
        },
        {
          title: 'Aseguradora',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'aseguradora',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Cliente',
          dataIndex: ['solicitud', 'cotizacion', 'persona', 'nombres'],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Marca',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'marca',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Modelo',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'modelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'SubModelo',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'submodelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Año',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'vehiculo',
            'aniosubmodelo',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Chapa',
          dataIndex: ['solicitud', 'cotizacion', 'vehiculo', 'chapa'],
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Fecha Solicitud',
          dataIndex: 'fecha_solicitud_inspeccion',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Fecha Fotografía',
          dataIndex: 'fecha_subida_fotos',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Fecha Informe',
          dataIndex: 'fecha_informe_inspeccion',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '30%'
        },
        {
          title: 'Estado',
          dataIndex: 'estado',
          render: (estado) => {
            const estadoUpperCase = toUpperCase(estado);
            let color = 'white';
            if (estadoUpperCase === 'PENDIENTE') {
              color = 'lightgray';
            } else if (estadoUpperCase === 'INICIADA') {
              color = 'yellow';
            } else if (estadoUpperCase === 'FOTOS_SUBIDAS') {
              color = 'blue';
            } else if (estadoUpperCase === 'AMPLIACION_PENDIENTE') {
              color = 'orange';
            } else if (estadoUpperCase === 'FINALIZADA') {
              color = 'green';
            }
            return (
              <Tag color={color} key={estadoUpperCase}>
                {estadoUpperCase}
              </Tag>
            );
          },
          filterType: fieldTypes.ENUM,
          filters: Object.keys(ESTADOS_INSPECCION).map((key) => ({
            text: ESTADOS_INSPECCION[key],
            value: key
          })),
          sortable: true
        },
        {
          title: 'Resultado',
          dataIndex: 'resultado',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              {!record.resultado ? (
                <Tooltip title="Editar Informe">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="middle"
                    disabled={
                      !(
                        record.estado !== 'PENDIENTE' ||
                        record.estado === 'AMPLIACION_PENDIENTE' ||
                        record.resultado
                      )
                    }
                    onClick={() =>
                      handleOpenUrl(`inspecciones/edit/${record.token}`)
                    }
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Ver Informe">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={() =>
                      handleOpenUrl(`inspecciones/edit/${record.token}`)
                    }
                  />
                </Tooltip>
              )}

              <Tooltip title="Descargar pdf">
                <Button
                  type="text"
                  icon={<FilePdfOutlined />}
                  size="middle"
                  loading={loadingPDFId === record.id}
                  disabled={!(record.estado === 'FINALIZADA')}
                  onClick={() => downloadPDF(record)}
                />
              </Tooltip>
              <Tooltip
                title={
                  record.estado === 'PENDIENTE'
                    ? 'Sin Fotografías'
                    : 'Descargar Fotografías'
                }
              >
                <Button
                  type="text"
                  icon={<CloudDownloadOutlined />}
                  size="middle"
                  loading={loadingId === record.id}
                  disabled={record.estado === 'PENDIENTE'}
                  onClick={() => downloadFiles(record, false, false)}
                />
              </Tooltip>
              <>
                <Tooltip
                  title={
                    record.estado === 'FOTOS_SUBIDAS' ||
                    record.solicitud.cotizacion.tipo_venta !== 'presencial'
                      ? `No es posible enviar ${
                          record.solicitud?.cotizacion?.aseguradoraEmpresa
                            ?.empresa?.tiene_whatsapp &&
                          record.solicitud.cotizacion.persona?.via_contacto ===
                            'whatsapp'
                            ? 'Whatsapp'
                            : 'SMS'
                        }`
                      : `Enviar por ${
                          record.solicitud?.cotizacion?.aseguradoraEmpresa
                            ?.empresa?.tiene_whatsapp &&
                          record.solicitud.cotizacion.persona?.via_contacto ===
                            'whatsapp'
                            ? 'Whatsapp'
                            : 'SMS'
                        } el enlace para subir fotos`
                  }
                >
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() => {
                      sendSms(record);
                    }}
                    icon={<MessageOutlined />}
                    size="middle"
                    disabled={
                      record.estado === 'FOTOS_SUBIDAS' ||
                      record.estado === 'FINALIZADA' ||
                      record.solicitud.cotizacion.tipo_venta !== 'presencial'
                    }
                  />
                </Tooltip>
                {record.estado === 'FOTOS_SUBIDAS' ||
                record.estado === 'FINALIZADA' ? (
                  <Tooltip title="No es posible copiar enlace">
                    <Button
                      disabled
                      icon={<CopyOutlined />}
                      type="text"
                      size="middle"
                    />
                  </Tooltip>
                ) : (
                  getCopyButtonComponent(record)
                )}
              </>
            </Space>
          )
        }
      ];
    }
    setColumns(columnas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingId, loadingPDFId, user]);

  const downloadFiles = async (record, isMarcadas, isCompress) => {
    try {
      setLoadingId(record.id);
      const responseArchivos = await instanceRef.instance.get(
        'archivos/get/zip',
        {
          params: {
            token: record.token,
            type: 'i',
            marked: isMarcadas,
            imgcompress: isCompress
          },
          headers: {
            'Content-Type': 'multipart/form-data',
            withCredentials: true
          },
          responseType: 'arraybuffer'
        }
      );
      await download(
        responseArchivos.data,
        'inspeccion.zip',
        'application/zip'
      );
    } catch (error) {
      errorHandler(error);
    }
    setLoadingId(0);
  };

  const downloadPDF = async (record) => {
    try {
      setLoadingPDFId(record.id);

      const response = await instanceRef.instance.get('inspeccion/get/pdf', {
        params: {
          token: record.token
        }
      });
      if (response.data && response.data.length === 0) {
        setLoadingPDFId(0);
        return;
      }
      const { filename, content } = response.data.data[0];
      const blob = b64toBlob(content, 'application/pdf');
      FileSaver.saveAs(blob, filename);
    } catch (error) {
      errorHandler(error);
    }
    setLoadingPDFId(0);
  };

  const sendSms = async (record) => {
    try {
      let newToken;
      let extraText = '';
      if (record.ampliacion) {
        newToken = record.ampliacion.token;
        extraText = ' de ampliación';
      } else {
        newToken = record.token;
      }
      const data = {
        persona: {
          celular: record.solicitud.cotizacion.persona.celular,
          via_contacto: record.solicitud.cotizacion.persona.via_contacto
        },
        token: newToken,
        idEmpresa: record.solicitud.cotizacion.aseguradoraEmpresa.id_empresa
      };
      message.info(
        `Se envió un mesaje de texto al ${record.solicitud.cotizacion.persona.celular} para subir las fotos${extraText}`
      );
      if (record.ampliacion !== null) {
        const reCaptchaToken = await executeRecaptcha('sms/ampliacion');
        data.reCaptchaToken = reCaptchaToken;
        await instanceRef.instance.post(`sms/ampliacion`, data);
      } else {
        const reCaptchaToken = await executeRecaptcha('sms/fotos');
        data.reCaptchaToken = reCaptchaToken;
        await instanceRef.instance.post(`sms/fotos`, data);
      }
    } catch (error) {
      // error
    }
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  return (
    <ResponsiveContainer size="full" style={{ paddingTop: 0 }}>
      <TableAndFilters
        withGeneralFilter
        ref={tableRef}
        url="/inspeccion"
        title="Inspecciones"
        columns={columns}
        showTable
        hasExportar={false}
      />
    </ResponsiveContainer>
  );
}

export default InspeccionList;
